import {
  $applyNodeReplacement,
  DOMConversionOutput,
  EditorConfig,
  NodeKey,
  SerializedTextNode,
  Spread,
  TextNode,
} from 'lexical'

interface IDetailType {
  id: string
  parentId: string
  displayName: string
}

export type SerializedSelectNode = Spread<
  {
    value: string
    moreDetails?: IDetailType
  },
  SerializedTextNode
>

export const createSelectNode = (value: string, moreDetails?: IDetailType) => {
  const selectNode = new SelectNode(value, moreDetails)

  selectNode.setMode('normal').toggleDirectionless()

  return $applyNodeReplacement(selectNode)
}

const convertSelectElement = (
  domNode: HTMLElement,
): DOMConversionOutput | null => {
  const textContent = domNode.textContent

  if (textContent !== null)
    return {
      node: createSelectNode(textContent),
    }

  return null
}

export class SelectNode extends TextNode {
  __value: string
  __moreDetails?: IDetailType

  // Return the type of the node
  static getType = () => 'select-node'

  // Clone the node
  static clone = (node: SelectNode) =>
    new SelectNode(node.__value, node.__moreDetails, node.__text, node.__key)

  // Import the serialized node
  static importJSON = (serializedNode: SerializedSelectNode) => {
    const node = createSelectNode(
      serializedNode.value,
      serializedNode.moreDetails,
    )

    node.setTextContent(serializedNode.text)
    node.setFormat(serializedNode.format)
    node.setDetail(serializedNode.detail)
    node.setMode(serializedNode.mode)
    node.setStyle(serializedNode.style)

    return node
  }

  constructor(
    value: string,
    moreDetails?: IDetailType,
    text?: string,
    key?: NodeKey,
  ) {
    super(text ?? value, key)

    this.__value = value
    this.__text =
      moreDetails?.displayName.replace(/ > /g, ' ').replace(/ /g, '_') || value
    this.__moreDetails = moreDetails
  }

  // Export the node to a serialized format
  exportJSON = (): SerializedSelectNode => ({
    ...super.exportJSON(),
    value: this.__value,
    moreDetails: this.__moreDetails,
    type: 'select-node',
    version: 1,
  })

  createDOM = (config: EditorConfig) => {
    const dom = super.createDOM(config)

    dom.setAttribute('data-lexical-select', 'true')

    return dom
  }

  isSegmented = () => true
}
